<template>
  <div>
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div class="flex justify-between items-center p-2">
          <div>
            <h3
              class="mb-0 w-full font-bold text-gray-800 text-md"
              style="text-transform: capitalize"
            >
              Inquiries List
            </h3>
          </div>
          <div class="flex items-center">
            <base-button size="sm" type="primary" @click="goToAddInquiry">
              Add New
            </base-button>
          </div>
        </div>

        <div class="px-3 py-3 bg-gray-50 border-b border-gray-200">
          <div class="flex flex-wrap gap-2">
            <button
              v-for="stat in statusBreakdown"
              :key="stat.statusCode"
              type="button"
              @click="applyStatusFilter(stat.statusCode)"
              class="flex flex-col justify-between items-center text-sm font-medium text-white rounded-md border-2 transition-all duration-150 hover:opacity-90 focus:outline-none"
              :class="
                columnFilters.status === stat.statusCode
                  ? 'border-blue-800 ring-2 ring-blue-400 ring-offset-1 scale-105'
                  : 'border-blue-400 hover:border-blue-600'
              "
            >
              <span class="px-3 py-2 bg-blue-500 w-full text-center">{{
                stat.statusLabel
              }}</span>
              <span
                class="inline-flex justify-center items-center px-3 py-2 text-sm font-bold text-black w-full"
              >
                {{ stat.count }}
              </span>
            </button>
          </div>
        </div>

        <div
          v-if="activeFilterChips.length"
          class="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-blue-100 bg-blue-50"
        >
          <span
            class="text-xs font-semibold text-blue-600 uppercase tracking-wide mr-1"
            >Filters:</span
          >
          <span
            v-for="chip in activeFilterChips"
            :key="chip.key"
            class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-800 bg-white border border-blue-200 rounded-full shadow-sm"
          >
            <span class="text-blue-400 mr-0.5">{{ chip.prefix }}</span
            >{{ chip.value }}
            <button
              type="button"
              @click="removeChip(chip.key)"
              class="ml-1 flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-white hover:bg-blue-500 transition-colors focus:outline-none"
            >
              ×
            </button>
          </span>
          <button
            type="button"
            @click="clearColumnFilters"
            class="ml-auto text-xs text-red-500 hover:text-red-700 font-medium underline focus:outline-none"
          >
            Clear all
          </button>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Number
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Created At
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Created By
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Customer
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Assigned Staff
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Status
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Actions
                </th>
              </tr>
              <tr class="bg-gray-100">
                <th class="px-1 py-2 border-b">
                  <input
                    v-model="columnFilters.inquiryNumber"
                    type="text"
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-1 py-2 border-b">
                  <input
                    v-model="columnFilters.createdAt"
                    type="date"
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @change="onColumnFilterChange"
                  />
                </th>
                <th class="px-1 py-2 border-b"></th>
                <th class="px-1 py-2 border-b">
                  <input
                    v-model="columnFilters.customer"
                    type="text"
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-1 py-2 border-b">
                  <input
                    v-model="columnFilters.staff"
                    type="text"
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-1 py-2 border-b"></th>
                <th class="px-1 py-2 border-b"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="px-2 py-4 text-xs text-center text-gray-500 border-b">
                  Loading...
                </td>
              </tr>
              <tr v-else-if="!tableData.length">
                <td colspan="7" class="px-2 py-4 text-xs text-center text-gray-500 border-b">
                  No inquiries found
                </td>
              </tr>
              <tr
                v-for="row in tableData"
                :key="row.inquiryId"
                class="hover:bg-gray-50"
              >
                <td class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b">
                  {{ row.inquiryNumber }}
                </td>
                <td class="px-2 py-2 text-xs text-gray-900 whitespace-nowrap border-b">
                  {{ formatDate(row.createdAt) }}
                </td>
                <td class="px-2 py-2 text-xs text-gray-900 border-b">
                  {{ row.createdByName || "—" }}
                </td>
                <td class="px-2 py-2 text-xs text-gray-900 border-b">
                  <div>{{ row.customerName || "—" }}</div>
                  <div v-if="row.customerPhone" class="text-gray-500 text-[11px]">
                    {{ row.customerPhone }}
                  </div>
                </td>
                <td class="px-2 py-2 text-xs text-gray-900 border-b">
                  <div>{{ row.staffName || "—" }}</div>
                  <div v-if="row.staffPhone" class="text-gray-500 text-[11px]">
                    {{ row.staffPhone }}
                  </div>
                </td>
                <td class="px-2 py-2 text-xs whitespace-nowrap border-b">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize"
                    :class="statusClass(row.status)"
                  >
                    {{ statusLabel(row.status) }}
                  </span>
                </td>
                <td class="px-2 py-2 text-xs whitespace-nowrap border-b">
                  <el-dropdown @command="cmd => handleAction(row, cmd)">
                    <el-button type="primary" size="mini">
                      Actions
                      <i class="el-icon-arrow-down el-icon--right" />
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="edit">Edit</el-dropdown-item>
                      <el-dropdown-item
                        v-if="row.status === 'pending'"
                        command="complete"
                      >
                        Mark Completed
                      </el-dropdown-item>
                      <el-dropdown-item
                        v-if="row.status === 'pending'"
                        command="cancel"
                      >
                        Mark Cancelled
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="meta.total" class="card-footer py-3">
          <pagination :meta="meta" @paginate="setPagination" />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Button
} from "element-ui";
import { mapGetters, mapActions } from "vuex";
import { format } from "date-fns";
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";

export default {
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Button.name]: Button,
    "header-component": headerComponent,
    pagination
  },
  data() {
    return {
      loading: false,
      pageSize: 50,
      filterTimeout: null,
      columnFilters: {
        inquiryNumber: "",
        createdAt: "",
        customer: "",
        staff: "",
        status: ""
      }
    };
  },
  computed: {
    ...mapGetters({
      inquiryList: "inquiry/getInquiryList",
      inquiryStats: "inquiry/getInquiryStats"
    }),
    tableData() {
      return this.inquiryList.rows || [];
    },
    meta() {
      const m = this.inquiryList.meta || {};
      return {
        total: m.total || 0,
        from: m.from,
        to: m.to,
        perPage: m.perPage || this.pageSize,
        currentPage: m.currentPage || 1,
        last: m.lastPage || m.last || 1
      };
    },
    statusBreakdown() {
      return this.inquiryStats.statusBreakdown || [];
    },
    activeFilterChips() {
      const chips = [];
      const cf = this.columnFilters;
      if (cf.inquiryNumber) {
        chips.push({ key: "inquiryNumber", prefix: "No:", value: cf.inquiryNumber });
      }
      if (cf.createdAt) {
        chips.push({ key: "createdAt", prefix: "Date:", value: cf.createdAt });
      }
      if (cf.customer) {
        chips.push({ key: "customer", prefix: "Customer:", value: cf.customer });
      }
      if (cf.staff) {
        chips.push({ key: "staff", prefix: "Staff:", value: cf.staff });
      }
      if (cf.status) {
        chips.push({
          key: "status",
          prefix: "Status:",
          value: this.statusLabel(cf.status)
        });
      }
      return chips;
    }
  },
  mounted() {
    this.paginate();
  },
  beforeDestroy() {
    clearTimeout(this.filterTimeout);
  },
  methods: {
    ...mapActions({
      getInquiryList: "inquiry/getInquiryList",
      updateInquiryStatus: "inquiry/updateInquiryStatus"
    }),

    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return format(d, "YYYY-MM-DD HH:mm");
    },

    statusLabel(code) {
      const map = {
        pending: "Pending",
        completed: "Completed",
        cancelled: "Cancelled"
      };
      return map[code] || code;
    },

    statusClass(code) {
      if (code === "completed") return "bg-green-100 text-green-800";
      if (code === "cancelled") return "bg-gray-100 text-gray-800";
      return "bg-red-100 text-red-800";
    },

    goToAddInquiry() {
      this.$router.push({ path: "/inquiries/add" });
    },

    setPagination(data) {
      this.pageSize = data.perPage;
      this.paginate(data.page);
    },

    paginate(page = 1) {
      this.loading = true;
      const query = {
        page,
        per_page: this.pageSize
      };
      const cf = this.columnFilters;
      if (cf.inquiryNumber) query.inquiry_number = cf.inquiryNumber;
      if (cf.createdAt) query.created_at = cf.createdAt;
      if (cf.customer) query.customer = cf.customer;
      if (cf.staff) query.staff = cf.staff;
      if (cf.status) query.status = cf.status;

      this.getInquiryList(query).finally(() => {
        this.loading = false;
      });
    },

    onColumnFilterChange() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => this.paginate(1), 400);
    },

    applyStatusFilter(code) {
      this.columnFilters.status =
        this.columnFilters.status === code ? "" : code;
      this.paginate(1);
    },

    removeChip(key) {
      if (key === "status") {
        this.columnFilters.status = "";
      } else {
        this.columnFilters[key] = "";
      }
      this.paginate(1);
    },

    clearColumnFilters() {
      this.columnFilters = {
        inquiryNumber: "",
        createdAt: "",
        customer: "",
        staff: "",
        status: ""
      };
      this.paginate(1);
    },

    handleAction(row, command) {
      if (command === "edit") {
        this.$router.push(`/inquiries/edit/${row.inquiryId}`);
      } else if (command === "complete") {
        this.setStatus(row, "completed");
      } else if (command === "cancel") {
        this.setStatus(row, "cancelled");
      }
    },

    setStatus(row, status) {
      this.updateInquiryStatus({ inquiryId: row.inquiryId, status })
        .then(() => {
          this.$notify({
            message: `Inquiry marked as ${this.statusLabel(status)}.`,
            type: "success",
            title: "Updated"
          });
          this.paginate(this.meta.currentPage || 1);
        })
        .catch(() => {
          this.$notify({
            message: "Could not update status.",
            type: "danger",
            title: "Error"
          });
        });
    }
  }
};
</script>
